// Admin log — sourced entirely from Storage.getAdminActivities() on-chain.
// The contract keeps the latest 100 activities per admin EOA in a circular
// buffer and exposes them via a paginated view. No PHP / off-chain storage.
import { useEffect, useState } from "react";
import { useWeb3 } from "../context/Web3Context";

// One-shot fetch (usable outside a component).
export async function fetchAdminLog({ contract, actor, limit = 100 } = {}) {
  if (!contract || !actor) return { entries: [], totalCount: 0 };
  try {
    const res = await contract.getAdminActivities(actor, 0, limit);
    const arr = res.activities || res[0] || [];
    const totalCount = res.totalCount ? Number(res.totalCount.toString()) : 0;
    const entries = arr.map((a) => normalize(a));
    return { entries, totalCount };
  } catch {
    return { entries: [], totalCount: 0 };
  }
}

// Convert on-chain Activity struct into a plain JS object.
function normalize(a) {
  const ts = a.timestamp?.toNumber ? a.timestamp.toNumber() : Number(a.timestamp?.toString() || 0);
  const type = a.activityType?.toNumber ? a.activityType.toNumber() : Number(a.activityType || 0);
  return {
    activityType: type,
    timestamp: ts,
    relatedId: a.relatedId?.toString() || "0",
    amount: a.amount,
    netAmount: a.netAmount,
  };
}

// React hook — returns newest-first, auto-refreshes and listens for
// `admin-log-updated` events fired by useAdminTx/useTx.
// autoRefresh: seconds between refreshes; 0 disables.
export function useAdminLog({ actor = null, limit = 30, autoRefresh = 30 } = {}) {
  const { getContract, isCorrectChain } = useWeb3();
  const [entries, setEntries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const contract = getContract();
      if (!contract || !actor || !isCorrectChain) {
        if (!cancelled) { setEntries([]); setTotalCount(0); setLoading(false); }
        return;
      }
      const { entries: e, totalCount: tc } = await fetchAdminLog({ contract, actor, limit });
      if (!cancelled) {
        setEntries(e);
        setTotalCount(tc);
        setLoading(false);
      }
    };
    load();
    let timer;
    if (autoRefresh > 0) timer = setInterval(load, autoRefresh * 1000);
    const onLog = () => load();
    window.addEventListener("admin-log-updated", onLog);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      window.removeEventListener("admin-log-updated", onLog);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, limit, autoRefresh, isCorrectChain]);

  return { entries, totalCount, loading };
}
